import React, { useState, useEffect, useMemo } from 'react';
import { Link } from "react-router-dom";
import axios from 'axios';
import SEO from '../../../components/common/SEO';
import { formatDate } from '../../../utils/dateUtils';
import { formatPrice } from '../../../utils/priceUtils';
import confirmDeletion from '../../utils/confirmDeletion';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
});

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

function ListingHolidayPackages() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'desc' });
  const pageSize = 20;

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/holiday-packages/');
        setPackages(response.data);
        setError(null);
      } catch (err) {
        setError("Failed to fetch packages. Please try again later.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchPackages();
  }, []);

  const sortedData = useMemo(() => {
    let filteredData = [...packages].filter(row =>
      (row.package_name && row.package_name.toLowerCase().includes(search.toLowerCase())) ||
      (row.city && row.city.toLowerCase().includes(search.toLowerCase()))
    );

    if (sortConfig.key) {
      // The default sort logic works for ISO date strings ('created_at') as well
      filteredData.sort((a, b) => {
        const valA = a[sortConfig.key] || '';
        const valB = b[sortConfig.key] || '';
        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filteredData;
  }, [packages, search, sortConfig]);

  const totalPages = Math.ceil(sortedData.length / pageSize);
  const paginatedData = sortedData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const renderSortArrow = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const handleDelete = async (packageId) => {
    if (!(await confirmDeletion())) return;
    try {
      await apiClient.delete(`/holiday-packages/${packageId}`);
      setPackages(currentPackages => currentPackages.filter(p => p.id !== packageId));
      setSuccessMessage('Package deleted successfully!');
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete package. Please try again.');
      console.error(err);
    }
  };

  const renderStatusBadge = (status, reason) => {
    let badgeClass = 'bg-secondary';
    if (status === 'Published') badgeClass = 'bg-success';
    else if (status === 'Under Review') badgeClass = 'bg-warning';
    else if (status === 'Rejected') badgeClass = 'bg-danger';

    return (
      <div>
        <span className={`badge ${badgeClass}`}>{status || 'Published'}</span>
        {status === 'Rejected' && reason && (
          <div className="text-danger mt-1" style={{ fontSize: '11px', whiteSpace: 'normal', maxWidth: '150px' }}>
            <i className="ri-error-warning-line align-middle me-1"></i>
            {reason}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="row">
      <SEO title="Holiday Packages Management | Mangoloops" />
      {/* <div className="col-12">
        <div className="page-title-box d-sm-flex align-items-center justify-content-between">
          <h4 className="mb-sm-0">My Holiday Packages</h4>
        </div>
      </div> */}
      <div className="col-lg-12">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
            <h4 className="card-title mb-0">My Holiday Packages</h4>
            <div className="d-flex flex-wrap gap-2">
              <input type="text" className="form-control" placeholder="Search..." value={search} onChange={handleSearchChange} style={{ width: '250px' }} />
              <Link to="/admin/add-holiday-packages" className="btn btn-soft-secondary">Add Holiday Package</Link>
            </div>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="text-center p-4"><div className="spinner-border" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : (
              <>
                {error && <div className="alert alert-danger">{error}</div>}
                {successMessage && <div className="alert alert-success">{successMessage}</div>}

                <div className="table-responsive">
                  <table className="table table-bordered table-striped align-middle">
                    <thead className="table-light">
                      <tr>
                        <th style={{ width: "80px" }}>Sr. No.</th>
                        <th style={{ width: "100px" }}>Image</th>
                        <th onClick={() => requestSort('package_name')} style={{ cursor: 'pointer' }}>Package Name{renderSortArrow('package_name')}</th>
                        <th onClick={() => requestSort('city')} style={{ cursor: 'pointer' }}>City{renderSortArrow('city')}</th>
                        <th onClick={() => requestSort('package_price')} style={{ cursor: 'pointer' }}>Price{renderSortArrow('package_price')}</th>
                        <th onClick={() => requestSort('offer_price')} style={{ cursor: 'pointer' }}>Offer Price{renderSortArrow('offer_price')}</th>
                        <th onClick={() => requestSort('status')} style={{ cursor: 'pointer' }}>Status{renderSortArrow('status')}</th>
                        <th onClick={() => requestSort('created_at')} style={{ cursor: 'pointer', width: "150px" }}>Created Date{renderSortArrow('created_at')}</th>
                        <th style={{ width: "100px" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedData.length > 0 ? paginatedData.map((row, index) => (
                        <tr key={row.id}>
                          <td>{(currentPage - 1) * pageSize + index + 1}</td>
                          <td>
                            {row.Images && row.Images.length > 0 ? (
                              <img
                                src={row.Images[0].image_url}
                                alt={row.package_name}
                                style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }}
                              />
                            ) : (
                              <span className="text-muted">No Image</span>
                            )}
                          </td>
                          <td>
                            <Link to={`/admin/edit-holiday-package/${row.id}`}>{row.package_name}</Link>
                          </td>
                          <td>{row.city}</td>
                          <td>₹{formatPrice(row.package_price)}</td>
                          <td>₹{formatPrice(row.offer_price)}</td>
                          <td>
                            {renderStatusBadge(row.status, row.rejection_reason)}
                          </td>
                          <td>
                            {formatDate(row.created_at)}
                          </td>
                          <td className='text-center'>
                            <button className="btn btn-sm btn-soft-danger" title="Delete" onClick={() => handleDelete(row.id)}>
                              <i className="ri-delete-bin-line"></i>
                            </button>
                          </td>
                        </tr>
                      )) : (
                        <tr><td colSpan="9" className="text-center">No matching records found</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {totalPages > 0 && (
                  <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                    <span className="text-muted">
                      Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, sortedData.length)} of {sortedData.length} entries
                    </span>
                    <nav>
                      <ul className="pagination pagination-sm mb-0">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>Previous</button>
                        </li>
                        {[...Array(totalPages).keys()].map(num => (
                          <li key={num + 1} className={`page-item ${currentPage === num + 1 ? 'active' : ''}`}>
                            <button className="page-link" onClick={() => setCurrentPage(num + 1)}>{num + 1}</button>
                          </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ListingHolidayPackages;
